/**
 * Lazy, client-only Firebase accessors for the standalone site-search app.
 *
 * Firebase is imported dynamically so it never lands in the SSR/server bundle:
 * every getter here must only be called from the browser (inside effects or
 * event handlers), never during render or on the server.
 *
 * The config mirrors `packages/constants/src/firebaseProjects.ts` and the
 * `firestoreAvatarAdapter` — these are public client values, not secrets.
 */

const FIREBASE_CONFIG = {
    projectId: "graviton-mvp",
    appId: "1:706308333141:web:9c78f0286da170472b3edc",
    apiKey: "AIzaSyAXNgauDwq62Ev-SVuub9VrKrGn2eCE6KE",
    authDomain: "auth.flux.ai",
    databaseURL: "https://graviton-mvp.firebaseio.com",
    storageBucket: "graviton-mvp.appspot.com",
    messagingSenderId: "706308333141",
    measurementId: "G-SZ17792FZ5",
} as const;

type FirebaseAppModule = typeof import("firebase/app");
type FirebaseAuthModule = typeof import("firebase/auth");
type FirebaseFirestoreModule = typeof import("firebase/firestore");
type FirebaseAuth = import("firebase/auth").Auth;

export interface FirebaseClient {
    readonly auth: import("firebase/auth").Auth;
    readonly db: import("firebase/firestore").Firestore;
    readonly authModule: FirebaseAuthModule;
    readonly firestoreModule: FirebaseFirestoreModule;
}

let clientPromise: Promise<FirebaseClient> | undefined;

/**
 * Resolve (and memoize) the shared Firebase app + auth + firestore handles.
 * Reuses the already-initialized app instance if one exists (the avatar adapter
 * and `useUserInfo` initialize the same app), so we never double-init.
 */
export function getFirebaseClient(): Promise<FirebaseClient> {
    if (!clientPromise) {
        clientPromise = (async (): Promise<FirebaseClient> => {
            const [firebaseApp, authModule, firestoreModule]: [
                FirebaseAppModule,
                FirebaseAuthModule,
                FirebaseFirestoreModule,
            ] = await Promise.all([import("firebase/app"), import("firebase/auth"), import("firebase/firestore")]);

            const existingApps = firebaseApp.getApps();
            const app =
                existingApps.length > 0
                    ? existingApps[0]!
                    : firebaseApp.initializeApp(FIREBASE_CONFIG, FIREBASE_CONFIG.projectId);

            return {
                auth: authModule.getAuth(app),
                db: firestoreModule.getFirestore(app),
                authModule,
                firestoreModule,
            };
        })();
    }

    return clientPromise;
}

/**
 * Wait for Firebase to restore persisted auth state, then return the current
 * signed-in user's ID token. Return `null` for a confirmed anonymous state.
 */
export async function getIdTokenAfterAuthReady(
    auth: Pick<FirebaseAuth, "authStateReady" | "currentUser">,
): Promise<string | null> {
    await auth.authStateReady();
    const user = auth.currentUser;
    if (!user || user.isAnonymous) {
        return null;
    }
    return user.getIdToken();
}

export async function getCurrentIdToken(): Promise<string | null> {
    const {auth} = await getFirebaseClient();
    return getIdTokenAfterAuthReady(auth);
}
